// Feuille de style de la page d'enchères, injectée une fois.
//
// ⚠️ AUCUNE couleur en dur : la page emprunte le vocabulaire des thèmes
// officiels (`--primary`, `--surface`, `--ink*`, `--line*`, `--r-*`,
// `--shadow-*`, `--font-display`), défini à l'identique par 2026
// (public/2026/css/2026.css) et par Travel (public/travel/css/travel.css).
// Posée dans l'un ou l'autre, la page prend sa palette, ses rayons, ses ombres
// et sa police de titres sans rien savoir du thème qui l'accueille.
//
// Les valeurs de repli ne servent qu'aux thèmes qui ne déclarent pas ces
// variables (Ummah aujourd'hui) : on y retombe sur une teinte neutre et sur la
// couleur de l'organisation, passée en `--ench-primary` par le composant.
//
// Tout est scopé `.ench`, et l'ajustement mobile se fait en CSS (pas en JS) :
// la page est d'abord consultée debout, sur un téléphone, avant Chabbat.
//
// ⚠️ Ce fichier est un template literal : ne JAMAIS écrire d'accent grave dans
// un commentaire CSS, cela fermerait la chaîne.
export const ENCH_CSS = `
.ench {
  --e-primary: var(--primary, var(--ench-primary, #2b70a9));
  --e-surface: var(--surface, #ffffff);
  --e-ink: var(--ink, #17324a);
  --e-ink2: var(--ink-2, #4b5a6b);
  --e-ink3: var(--ink-3, #617284);
  --e-line: var(--line, #e2eaf1);
  --e-line2: var(--line-2, #cfd9e3);
  --e-r: var(--r-lg, 18px);
  --e-r-sm: var(--r-sm, 10px);
  --e-pill: var(--r-pill, 999px);
  --e-shadow: var(--shadow-md, 0 8px 22px -14px rgba(20,24,31,.22), 0 2px 4px rgba(20,24,31,.06));
  --e-display: var(--font-display, inherit);
  /* Teinte douce dérivée de l'accent : remplace les bleus pâles en dur, qui
     juraient sur le parchemin du thème 2026. */
  --e-soft: color-mix(in oklab, var(--e-primary) 8%, var(--e-surface));
  --e-soft2: color-mix(in oklab, var(--e-primary) 14%, var(--e-surface));

  max-width: min(var(--container, 1100px), 100%);
  margin: 0 auto;
  padding: clamp(20px, 4vw, 40px) clamp(16px, 4vw, 32px) 64px;
  color: var(--e-ink);
}
.ench * { box-sizing: border-box; }

/* ── En-tête de la vente ──────────────────────────────────────────────── */
.ench-entete { margin-bottom: 22px; }
.ench-titre {
  font-family: var(--e-display);
  font-size: clamp(26px, 3.6vw, 40px);
  font-weight: 700;
  letter-spacing: -.02em;
  line-height: 1.15;
  color: var(--e-ink);
  text-wrap: balance;
}
/* L'occasion se lit comme une sur-titre du thème : petite, espacée, discrète. */
.ench-occasion {
  display: block;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: var(--e-ink3);
  margin-bottom: 8px;
}

/* Se faire reconnaître sans enchérir : discret tant que la vente est ouverte,
   mais c'est la seule porte qui reste une fois les lots adjugés. */
.ench-identifier { margin: -6px 0 18px; }
.ench-lien {
  appearance: none; background: none; border: 0; padding: 0;
  font: inherit; font-size: 14px; font-weight: 600;
  color: var(--e-primary); cursor: pointer;
  text-decoration: underline; text-underline-offset: 3px;
}
.ench-lien:hover { opacity: .8; }

.ench-compte {
  display: inline-flex; align-items: center; gap: 10px; margin-top: 16px;
  padding: 9px 16px; border-radius: var(--e-pill);
  background: var(--e-soft); border: 1px solid var(--e-line);
  color: var(--e-ink2); font-size: 14px; font-weight: 600;
}
.ench-compte-fin {
  color: #8c2f2f; background: color-mix(in oklab, #c0392b 10%, var(--e-surface));
  border-color: color-mix(in oklab, #c0392b 26%, var(--e-surface));
}

.ench-bandeau {
  display: flex; align-items: center; gap: 12px; padding: 13px 16px;
  border-radius: var(--e-r); margin-bottom: 14px; font-size: 14px; line-height: 1.45;
}
.ench-bandeau-info { background: var(--e-soft); color: var(--e-ink2); border: 1px solid var(--e-line); }
.ench-bandeau-alerte {
  background: color-mix(in oklab, #b8860b 10%, var(--e-surface));
  color: #7a5a12; border: 1px solid color-mix(in oklab, #b8860b 28%, var(--e-surface));
}
.ench-bandeau-succes {
  background: color-mix(in oklab, #1d7a55 10%, var(--e-surface));
  color: #17553c; border: 1px solid color-mix(in oklab, #1d7a55 26%, var(--e-surface));
}

.ench-jour {
  font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .12em;
  color: var(--e-ink3); margin: 26px 0 12px;
}

/* ── Les lots ─────────────────────────────────────────────────────────── */
.ench-lots { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 14px; }

.ench-lot {
  border: 1px solid var(--e-line); border-radius: var(--e-r); padding: 18px;
  background: var(--e-surface); box-shadow: var(--e-shadow);
  display: flex; flex-direction: column; gap: 8px;
  transition: box-shadow .2s ease, border-color .2s ease;
}
.ench-lot:hover { box-shadow: var(--shadow-lg, 0 30px 60px -28px rgba(20,24,31,.32)); }
.ench-lot-mene { border-color: color-mix(in oklab, #1d7a55 45%, var(--e-line)); }
.ench-lot-depasse { border-color: color-mix(in oklab, #b8860b 45%, var(--e-line)); }
.ench-lot-haut { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; }
.ench-lot-titre {
  font-family: var(--e-display);
  font-size: 20px; font-weight: 700; letter-spacing: -.01em; color: var(--e-ink);
}
.ench-lot-office {
  font-size: 11.5px; font-weight: 600; letter-spacing: .1em; text-transform: uppercase;
  color: var(--e-ink3); margin-top: 4px;
}
.ench-lot-sens { font-size: 14px; color: var(--e-ink2); line-height: 1.55; }
.ench-lot-corps, .ench-lot-surplace { margin-top: 4px; }
.ench-lot-montant {
  font-family: var(--e-display);
  font-size: 30px; font-weight: 700; letter-spacing: -.02em; color: var(--e-ink); line-height: 1.1;
}
.ench-lot-vide { font-size: 15px; font-weight: 600; color: var(--e-ink3); }
.ench-lot-meneur { font-size: 13.5px; color: var(--e-ink2); margin-top: 3px; }
.ench-lot-alerte { font-size: 13.5px; font-weight: 700; color: #8a6510; margin-top: 5px; }
.ench-lot-plafond { font-size: 12.5px; color: var(--e-ink3); margin-top: 2px; }
.ench-lot-mention { font-size: 13.5px; color: var(--e-ink3); }
.ench-lot-resultat {
  font-size: 14px; font-weight: 700; color: #17553c;
  background: color-mix(in oklab, #1d7a55 10%, var(--e-surface));
  border-radius: var(--e-r-sm); padding: 9px 12px;
}
.ench-lot-restant { font-size: 12.5px; color: var(--e-ink3); }
.ench-lot-actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 6px; }

/* Les badges (tribu, mode de vente) reprennent la pastille des thèmes. */
.ench-lot-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 11px; border-radius: var(--e-pill);
  font-size: 11.5px; font-weight: 700; letter-spacing: .04em;
  background: var(--e-soft2); color: var(--e-primary);
  border: 1px solid color-mix(in oklab, var(--e-primary) 22%, transparent);
  white-space: nowrap;
}

/* ── Boutons : la pastille pleine des thèmes officiels ────────────────── */
.ench-btn {
  flex: 1; min-width: 140px; border: 1px solid transparent; border-radius: var(--e-pill);
  padding: 12px 20px; font-size: 15px; font-weight: 600; letter-spacing: .01em;
  font-family: inherit; cursor: pointer;
  background: var(--e-primary); color: #fff;
  box-shadow: 0 6px 14px -8px color-mix(in oklab, var(--e-primary) 70%, black);
  transition: background .15s ease, transform .1s ease;
}
.ench-btn:hover { background: color-mix(in oklab, var(--e-primary) 88%, black); }
.ench-btn:active { transform: translateY(1px); }
.ench-btn-secondaire {
  background: transparent; color: var(--e-primary);
  border-color: color-mix(in oklab, var(--e-primary) 32%, transparent); box-shadow: none;
}
.ench-btn-secondaire:hover { background: var(--e-soft); }

/* ── S'identifier ─────────────────────────────────────────────────────── */
.ench-auth { max-width: 460px; margin: 0 auto; }
.ench-auth-titre {
  font-family: var(--e-display);
  font-size: 22px; font-weight: 700; letter-spacing: -.01em; color: var(--e-ink); margin-bottom: 6px;
}
.ench-auth-sous { font-size: 14px; color: var(--e-ink3); line-height: 1.55; margin-bottom: 18px; }
.ench-auth-ligne { display: flex; gap: 12px; }
.ench-auth-ligne > * { flex: 1; }

/* ── La feuille de mise ───────────────────────────────────────────────── */
.ench-sheet-actuel { font-size: 15px; color: var(--e-ink2); margin-bottom: 16px; }
.ench-paliers { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 16px; }
.ench-palier {
  flex: 1; min-width: 96px; border: 1px solid var(--e-line); background: var(--e-surface);
  border-radius: var(--e-r); padding: 15px 10px;
  font-family: var(--e-display); font-size: 19px; font-weight: 700; letter-spacing: -.01em;
  cursor: pointer; color: var(--e-ink); transition: border-color .15s ease, background .15s ease;
}
.ench-palier:hover { border-color: var(--e-line2); }
.ench-palier-actif {
  border-color: var(--e-primary); background: var(--e-soft);
  box-shadow: 0 0 0 1px var(--e-primary) inset;
}
.ench-sheet-switch {
  display: flex; align-items: flex-start; gap: 12px;
  margin: 2px 0 20px; font-size: 14px; color: var(--e-ink2); line-height: 1.5;
}
.ench-lien-sortie { display: block; margin: 18px auto 4px; font-weight: 500; color: var(--e-ink3); }

.ench-sheet-libre {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 12px 0; font-size: 14px; color: var(--e-ink2);
}
.ench-sheet-option {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 14px;
  padding: 14px 0; border-top: 1px solid var(--e-line);
}
.ench-sheet-option-titre { font-size: 14.5px; font-weight: 700; color: var(--e-ink); }
.ench-sheet-option-aide { font-size: 12.5px; color: var(--e-ink3); line-height: 1.55; margin-top: 3px; }

.ench-confirm {
  border-radius: var(--e-r); background: var(--e-soft);
  border: 1px solid var(--e-line); padding: 18px; margin-bottom: 18px;
}
.ench-confirm-titre {
  font-family: var(--e-display);
  font-size: 19px; font-weight: 700; color: var(--e-ink); line-height: 1.35; letter-spacing: -.01em;
}
.ench-confirm-detail { font-size: 13px; color: var(--e-ink2); margin-top: 8px; }

/* ── Mes enchères ─────────────────────────────────────────────────────── */
.ench-mes { display: flex; flex-direction: column; gap: 10px; }
.ench-mes-ligne {
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  border: 1px solid var(--e-line); border-radius: var(--e-r); padding: 14px 16px;
  background: var(--e-surface);
}
.ench-mes-label { font-weight: 700; color: var(--e-ink); }
.ench-mes-etat { font-size: 13px; color: var(--e-ink3); margin-top: 2px; }

.ench-vide { text-align: center; color: var(--e-ink3); padding: 48px 16px; }
.ench-vide-titre {
  font-family: var(--e-display);
  font-size: 20px; font-weight: 700; color: var(--e-ink2); margin-bottom: 8px;
}

/* ── Les quelques composants antd visibles prennent la même peau ──────── */
.ench .ant-drawer-content, .ench-sheet .ant-drawer-content { background: var(--e-surface); }
.ench-sheet .ant-drawer-header { border-bottom-color: var(--e-line); }
.ench-sheet .ant-drawer-title { font-family: var(--e-display); font-weight: 700; color: var(--e-ink); }
.ench .ant-segmented { background: var(--e-soft); border-radius: var(--e-pill); padding: 4px; }
.ench .ant-segmented-item { border-radius: var(--e-pill); }
.ench .ant-segmented-item-selected { background: var(--e-surface); }
.ench .ant-btn-primary {
  border-radius: var(--e-pill); background: var(--e-primary); border-color: var(--e-primary);
  font-weight: 600; box-shadow: 0 6px 14px -8px color-mix(in oklab, var(--e-primary) 70%, black);
}
.ench .ant-input, .ench .ant-input-number, .ench .ant-input-affix-wrapper {
  border-radius: var(--e-r-sm); border-color: var(--e-line);
}

@media (max-width: 560px) {
  .ench { padding: 18px 16px 56px; }
  .ench-lots { grid-template-columns: 1fr; }
  .ench-auth-ligne { flex-direction: column; gap: 0; }
  .ench-lot { padding: 16px; }
  .ench-lot-montant { font-size: 26px; }
}
`
